import { useLocation, useParams } from 'react-router-dom'
import type { ResourceScope } from '../api/resources'
import { ResourceFormContent } from '../components/ResourceFormContent'
import { WsPageShell } from '../components/WsPageShell'
import { ROUTES } from '../constants/routes'
import { resourcesCopy } from '../copy/resources'
import { useMeQuery } from '../hooks/queries'
import { useGoBack } from '../hooks/useGoBack'
import { useTopicsQuery } from '../hooks/useTopicsQuery'
import { useWorkspaceStore } from '../store/workspace'

export function ResourceFormPage() {
  const { topicId, workspaceId, resourceId } = useParams<{
    topicId?: string
    workspaceId?: string
    resourceId?: string
  }>()
  const { state } = useLocation() as { state: { returnTo?: string } | null }
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId)
  const { data: me } = useMeQuery()
  const wsName =
    me?.workspaces.find((w) => w.workspace_id === activeWorkspaceId)?.name ??
    resourcesCopy.list.workspaceCrumb
  const { data: topics = [] } = useTopicsQuery(activeWorkspaceId)
  const topic = topics.find((t) => t.topic_id === topicId)

  const scope: ResourceScope = workspaceId
    ? { kind: 'workspace', id: workspaceId }
    : { kind: 'topic', id: topicId ?? '' }
  const isEdit = !!resourceId

  // History-back keeps the list's scroll position when the user came from it;
  // the fallback only fires on a direct load, where the scope's own list beats
  // dumping the user at the app root.
  const listUrl = workspaceId
    ? ROUTES.workspaceResources(workspaceId)
    : `${ROUTES.topic(topicId ?? '')}?tab=resources`
  const goBack = useGoBack(state?.returnTo ?? listUrl)

  return (
    <WsPageShell
      crumbs={[
        { label: wsName, to: ROUTES.home },
        ...(topic
          ? [{ label: topic.name, to: ROUTES.topic(topic.topic_id) }]
          : []),
        { label: resourcesCopy.list.crumb, to: listUrl },
        {
          label: isEdit
            ? resourcesCopy.form.crumbEdit
            : resourcesCopy.form.crumbAdd,
        },
      ]}
      title={isEdit ? resourcesCopy.form.editTitle : resourcesCopy.add}
      description={isEdit ? undefined : resourcesCopy.form.addDescription}
    >
      <ResourceFormContent
        scope={scope}
        resourceId={resourceId}
        onDone={goBack}
      />
    </WsPageShell>
  )
}
